import { cn } from "@babybjorn/ui";
import { useEffect, useRef, useState, type FC, type MouseEvent } from "react";
import Button from "../../components/general/Button";
import type { ButtonProps } from "../../components/general/Button/Button";
import * as s from "./AddToCartButton.module.scss";

export enum State {
	Idle = "idle",
	Pending = "pending",
	Added = "added",
}

type AddToCartButtonProps = ButtonProps & {
	onClick?: (event: MouseEvent<HTMLButtonElement>) => Promise<void>;
	state?: State;
	divClassContainer?: string;
};

const AddToCartButton: FC<AddToCartButtonProps> = ({
	onClick,
	className,
	label,
	fullWidth,
	state,
	divClassContainer,
	...rest
}) => {
	const [displayState, setDisplayState] = useState(state ?? State.Idle);
	const [minWidth, setMinWidth] = useState<string>("auto");
	const ref = useRef<HTMLButtonElement | null>(null);

	const click = (event: MouseEvent<HTMLButtonElement>) => {
		// Make sure button width don't collpase when changing state
		if (!fullWidth && ref.current) {
			const w = ref.current.getBoundingClientRect().width;
			if (w) setMinWidth(`${w}px`);
		}
		if (!onClick) return;
		setDisplayState(State.Pending);

		try {
			onClick(event);
			setDisplayState(State.Added);
		} catch {
			setDisplayState(State.Idle);
		}
	};

	useEffect(() => {
		// Always reset when in 'Added state'
		if (displayState === State.Added) {
			setTimeout(() => {
				setDisplayState(State.Idle);
				setMinWidth("auto");
			}, 1000);
		}
	}, [displayState]);

	useEffect(() => {
		if (state) setDisplayState(state);
	}, [state]);

	return (
		<div
			className={cn(s.addToCartButton, divClassContainer)}
			style={{ minWidth }}
		>
			<Button
				{...rest}
				ref={ref}
				fullWidth={rest.buttonType !== "icon"}
				className={cn(
					className,
					displayState === State.Idle ? undefined : s[displayState],
				)}
				variant="primary"
				loading={displayState === State.Pending}
				iconSize={displayState === State.Idle ? undefined : 30}
				label={
					displayState === State.Idle && rest.buttonType !== "icon"
						? label
						: undefined
				}
				icon={
					displayState === State.Added
						? "checkmark"
						: rest.buttonType === "icon"
							? "cart-add"
							: "cart"
				}
				onClick={(event) => void click(event)}
			/>
		</div>
	);
};

export default AddToCartButton;
